import {
  AppointmentOriginal,
  AppointmentPreviewData
} from 'components/mrp/stores/appointmentStore/appointmentStore.types';
import {
  CalendarAppointmentEvent,
  CalendarAppointmentPreviewEvent
} from './Calendar.types';
import {
  DAY_EVENTS,
  IS_PREVIEW_ID
} from '../AppointmentForm/AppointmentForm.consts';
import dayjs from 'dayjs';
import { useCalendarStore } from 'components/mrp/stores/calendarStore/calendarStore';
import { useShallow } from 'zustand/shallow';
import { useDateNavigationStore } from 'components/shared/DateNavigation/DateNavigation.stores';
import useSelectedDate from 'components/roster-portal/activity-planning/hooks/useSelectedDate';
import useSelectedDateRange from 'components/roster-portal/activity-planning/hooks/useSelectedDateRange';
import { useCalendarNavigationStore } from '../CalendarContainer/components/store/calendarNavigationStore.store';
import { useAppointmentStore } from 'components/mrp/stores/appointmentStore/appointmentStore';
import { PlanningEntityIdentifier } from 'generated/index';
import { useEntityListener } from 'components/mrp/mrp.hooks';

export const useEntityCalendarHooks = (
  calendarId: string,
  planningEntityIdentifiers: PlanningEntityIdentifier[]
) => {
  const { timeStep, calendar, timeSlotMin, timeSlotMax, viewState } =
    useCalendarStore(
      useShallow((state) => ({
        timeSlotMin: state.timeSlotMin,
        timeSlotMax: state.timeSlotMax,
        timeStep: state.timeStep,
        calendar: state.calendars[calendarId],
        viewState: state.viewState
      }))
    );

  const periodType = useDateNavigationStore((state) => state.period);
  const [, setSelectedDate] = useSelectedDate();
  const { startDate, endDate } = useSelectedDateRange();

  const { setEvents, setEvent } = useCalendarNavigationStore(
    useShallow((state) => ({
      setEvents: state.control.setCalendarEvents,
      setEvent: state.control.setEvent
    }))
  );

  const {
    appointment,
    original,
    previewData,
    getPreviewData,
    setAppointmentData
  } = useAppointmentStore(
    useShallow((state) => ({
      previewData: state.previewData,
      original: state.original,
      appointment: state.appointment,
      getPreviewData: state.control.getPreviewData,
      setAppointmentData: state.control.setAppointmentData
    }))
  );

  const { events, availabilities, isError, refetch } = useEntityListener({
    calendarId,
    planningEntityIdentifiers
  });

  const setPosition = useCalendarNavigationStore(
    (state) => state.control.setPosition
  );

  return {
    timeStep,
    calendar,
    timeSlotMax,
    timeSlotMin,
    periodType,
    startDate,
    endDate,
    appointment,
    original,
    previewData,
    events,
    availabilities,
    isError,
    viewState,
    refetch,
    setSelectedDate,
    setEvent,
    setEvents,
    getPreviewData,
    setAppointmentData,
    setPosition
  };
};

const getUniqueEvents = (events: CalendarAppointmentEvent[]) => {
  const eventIds = new Set<string | number>();

  const uniqueEvents = events.filter(({ id }) => {
    if (eventIds.has(id)) return false;

    eventIds.add(id);
    return true;
  });

  return uniqueEvents;
};

export const getCalendarEvents = ({
  previewEvents,
  events,
  original,
  calendarId
}: {
  previewEvents: AppointmentPreviewData[];
  events: CalendarAppointmentEvent[];
  original: AppointmentOriginal | undefined;
  calendarId: string;
}) => {
  const previewedEventIds = previewEvents.map(({ id }) => id);
  const filteredEvents = events.filter(
    ({ id }) =>
      previewedEventIds.indexOf(id) === -1 && (!original || original.id !== id)
  );

  const uniqueEvents = getUniqueEvents(filteredEvents);

  const mappedPreviewEvents = previewEvents.map((preview) => ({
    id: IS_PREVIEW_ID,
    start: new Date(preview.start || Date.now()),
    end: new Date(preview.end || Date.now()),
    calendarId,
    hasOverlap: uniqueEvents.some(
      (evt) =>
        dayjs(preview.start).isBefore(dayjs(evt.end)) &&
        dayjs(evt.start).isBefore(preview.end)
    )
  })) as CalendarAppointmentPreviewEvent[];

  return [
    ...(mappedPreviewEvents as CalendarAppointmentEvent[]),
    ...uniqueEvents
  ].map((event) => ({
    ...event,
    resourceId: DAY_EVENTS
  }));
};
